import axios from 'axios';

import API_URL from '../../../config/api.js';

const getToken = () => {
	try {
		return JSON.parse(atob(localStorage.getItem('token') || '""'));
	} catch (error) {
		console.error('Erro ao ler token para revisão de produto:', error);
		return null;
	}
};

const authHeaders = () => ({
	Authorization: `Bearer ${getToken()}`,
});

export const fetchServerDateTime = async () => {
	const response = await axios.get(
		`${API_URL}/api/revisao-produto/horario-atual`,
		{
			headers: authHeaders(),
		}
	);

	return response.data?.dataHora ?? null;
};

export const fetchPendingReviewDetail = async ({
	serviceKey,
	be,
	bm,
	lote,
	bloco,
}) => {
	const response = await axios.get(`${API_URL}/api/revisao-produto/detalhe`, {
		params: {
			serviceKey,
			be,
			bm,
			lote,
			bloco,
		},
		headers: authHeaders(),
	});

	return response.data;
};

export const completePendingReview = async ({ reviewId, version, itemIds }) => {
	const response = await axios.post(
		`${API_URL}/api/revisao-produto/${reviewId}/concluir`,
		{
			versao: version,
			itemIds,
		},
		{
			headers: authHeaders(),
		}
	);

	return response.data;
};

export const reopenPendingReview = async reviewId => {
	const response = await axios.post(
		`${API_URL}/api/revisao-produto/${reviewId}/reabrir`,
		null,
		{ headers: authHeaders() }
	);

	return response.data;
};

const getDownloadFileName = (contentDisposition, fallback) => {
	if (!contentDisposition) return fallback;

	const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
	if (utf8Match?.[1]) {
		try {
			return decodeURIComponent(utf8Match[1].replace(/["']/g, ''));
		} catch (_) {
			return utf8Match[1].replace(/["']/g, '');
		}
	}

	const simpleMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
	return simpleMatch?.[1]?.trim() || fallback;
};

export const downloadPendingReviewReport = async (
	reviewId,
	fallbackName = null
) => {
	const response = await axios.get(
		`${API_URL}/api/revisao-produto/${reviewId}/relatorio`,
		{
			headers: authHeaders(),
			responseType: 'blob',
		}
	);

	const defaultName = fallbackName || `Comprovante_Revisao_${reviewId}.pdf`;

	const fileName = getDownloadFileName(
		response.headers?.['content-disposition'],
		defaultName
	);

	const blob = new Blob([response.data], {
		type: 'application/pdf',
	});

	const objectUrl = window.URL.createObjectURL(blob);
	const link = document.createElement('a');

	link.href = objectUrl;
	link.download = fileName;

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);

	setTimeout(() => {
		window.URL.revokeObjectURL(objectUrl);
	}, 1000);

	return fileName;
};

const reviewStatusCache = new Map();

const normalizeReviewBe = value => {
	const text = String(value ?? '')
		.trim()
		.toUpperCase();

	const digits = text.replace(/\D/g, '');
	if (!digits) return text;

	const parsed = Number.parseInt(digits, 10);
	if (Number.isNaN(parsed)) return text;

	return `BE${String(parsed).padStart(2, '0')}`;
};

const normalizeReviewLote = value => {
	const digits = String(value ?? '').replace(/\D/g, '');
	if (!digits) return null;

	const parsed = Number.parseInt(digits, 10);
	return Number.isNaN(parsed) ? null : parsed;
};

const normalizeReviewBloco = value =>
	String(value ?? '')
		.trim()
		.toUpperCase();

const buildReviewKey = (be, lote, bloco) =>
	[
		normalizeReviewBe(be),
		normalizeReviewLote(lote),
		normalizeReviewBloco(bloco),
	].join('|');

const findUniqueReviewByLoteBloco = (statuses, lote, bloco) => {
	const normalizedLote = normalizeReviewLote(lote);
	const normalizedBloco = normalizeReviewBloco(bloco);

	if (normalizedLote === null || !normalizedBloco) {
		return null;
	}

	const matches = [];

	for (const review of statuses.values()) {
		if (
			normalizeReviewLote(review?.lote) === normalizedLote &&
			normalizeReviewBloco(review?.bloco) === normalizedBloco
		) {
			matches.push(review);

			if (matches.length > 1) {
				return null;
			}
		}
	}

	return matches[0] || null;
};

export const fetchReviewStatusesForService = async (
	serviceKey,
	force = false
) => {
	if (!serviceKey) return new Map();

	if (!force && reviewStatusCache.has(serviceKey)) {
		return reviewStatusCache.get(serviceKey);
	}

	try {
		const response = await axios.get(
			`${API_URL}/api/revisao-produto/status-servico`,
			{
				params: { serviceKey },
				headers: authHeaders(),
			}
		);

		const statusMap = new Map();

		(response.data || []).forEach(review => {
			const key = buildReviewKey(review.be, review.lote, review.bloco);

			statusMap.set(key, review);
		});

		reviewStatusCache.set(serviceKey, statusMap);
		return statusMap;
	} catch (error) {
		console.warn(
			`Não foi possível sincronizar estados de revisão de ${serviceKey}:`,
			error
		);
		return reviewStatusCache.get(serviceKey) || new Map();
	}
};

export const applyReviewStatusesToFeatures = async (features, serviceKey) => {
	if (!Array.isArray(features) || features.length === 0 || !serviceKey) {
		return features;
	}

	const statuses = await fetchReviewStatusesForService(serviceKey);

	features.forEach(feature => {
		const properties = feature?.properties;
		if (!properties) return;

		const be = properties.be ?? properties.BE;
		const lote = properties.lote ?? properties.LOTE;
		const bloco =
			properties.blocos ??
			properties.bloco ??
			properties.BLOCOS ??
			properties.BLOCO;

		const key = buildReviewKey(be, lote, bloco);

		const review =
			statuses.get(key) ||
			findUniqueReviewByLoteBloco(statuses, lote, bloco);

		if (!review) {
			return;
		}

		properties._revisao_id = review.id;
		properties._revisao_status = review.statusRevisao;
		properties._revisao_versao = review.versao;
	});

	return features;
};

export const updateReviewStatusCache = review => {
	if (!review?.serviceKey) return;

	let statusMap = reviewStatusCache.get(review.serviceKey);
	if (!statusMap) {
		statusMap = new Map();
		reviewStatusCache.set(review.serviceKey, statusMap);
	}

	const key = buildReviewKey(review.be, review.lote, review.bloco);

	statusMap.set(key, review);
};
